import { buildWebSocketUrl } from './getServerInfo';
import { debugLog, debugWarn } from './debug';

/**
 * Tests a single camera's WebSocket endpoint
 * @param {number} cameraId - Camera ID to test
 * @param {Object} auth - Authentication object (session or username/password)
 * @param {number} timeout - Timeout in ms
 * @param {Object} options - Optional host/port overrides
 * @returns {Promise<{success: boolean, cameraId: number, error?: string}>}
 */
const testCameraWebSocket = (cameraId, auth, timeout, options = {}) => {
    return new Promise((resolve) => {
        // Use profile 1 - it always exists (profile 2 may not exist on ~5% of cameras)
        const testUrl = buildWebSocketUrl(cameraId, 1, auth, options);

        // Log URL without credentials
        const logUrl = testUrl.split('?')[0];
        debugLog(`[WebSocket Detection] Testing camera ${cameraId}: ${logUrl}`);

        let ws;
        let timeoutId;
        let resolved = false;

        const cleanup = () => {
            if (resolved) return;
            resolved = true;
            clearTimeout(timeoutId);
            if (ws) {
                try {
                    ws.onopen = null;
                    ws.onerror = null;
                    ws.onclose = null;
                    ws.close();
                } catch (e) {
                    // Ignore cleanup errors
                }
            }
        };

        // Set timeout
        timeoutId = setTimeout(() => {
            cleanup();
            debugLog(`[WebSocket Detection] Camera ${cameraId}: Timeout`);
            resolve({ success: false, cameraId, error: 'timeout' });
        }, timeout);

        try {
            ws = new WebSocket(testUrl);

            ws.onopen = () => {
                cleanup();
                debugLog(`[WebSocket Detection] Camera ${cameraId}: ✓ Connected`);
                resolve({ success: true, cameraId });
            };

            ws.onerror = (error) => {
                cleanup();
                debugLog(`[WebSocket Detection] Camera ${cameraId}: ✗ Error`, error);
                resolve({ success: false, cameraId, error: 'connection_error' });
            };

            ws.onclose = (event) => {
                cleanup();
                // Standard close codes (1000-1015) mean endpoint exists
                if (event.code >= 1000 && event.code <= 1015) {
                    debugLog(`[WebSocket Detection] Camera ${cameraId}: ✓ Endpoint exists (close code: ${event.code})`);
                    resolve({ success: true, cameraId });
                } else if (event.code >= 4000) {
                    debugLog(`[WebSocket Detection] Camera ${cameraId}: Backend error (code: ${event.code})`);
                    resolve({ success: false, cameraId, error: event.reason || `code_${event.code}` });
                } else {
                    debugLog(`[WebSocket Detection] Camera ${cameraId}: ✓ Endpoint exists`);
                    resolve({ success: true, cameraId });
                }
            };

        } catch (error) {
            cleanup();
            const errorMsg = error.message || String(error);
            debugLog(`[WebSocket Detection] Camera ${cameraId}: ✗ Exception: ${errorMsg}`);
            resolve({ success: false, cameraId, error: errorMsg });
        }
    });
};

/**
 * Detects if the backend server supports websocket streaming
 * Tests up to 2 enabled cameras in parallel for reliability
 *
 * @param {Object} auth - Authentication object
 * @param {string} [auth.session] - Session token (preferred)
 * @param {string} [auth.username] - Username (fallback)
 * @param {string} [auth.password] - Password (fallback)
 * @param {Array<{bID: number, fEnable: boolean}>} cameras - Array of camera objects (from server state)
 * @param {Object} options - Optional configuration
 * @param {number} [options.timeout=3000] - Timeout in ms
 * @param {string} [options.host] - Override host
 * @param {string} [options.port] - Override port
 * @returns {Promise<{supported: boolean, testedCameras: number[], error?: string}>}
 */
export const detectWebSocketSupport = async (auth, cameras = [], options = {}) => {
    const timeout = options.timeout || 3000;

    // Get up to 2 enabled cameras to test
    const enabledCameras = cameras
        .filter(c => c.fEnable)
        .slice(0, 2)
        .map(c => c.bID);

    // Fallback to camera 1 if no camera list provided
    if (enabledCameras.length === 0) {
        debugLog('[WebSocket Detection] No enabled cameras provided, falling back to camera 1');
        enabledCameras.push(1);
    }

    debugLog(`[WebSocket Detection] Testing cameras: [${enabledCameras.join(', ')}]`);

    // Test cameras in parallel
    const results = await Promise.all(
        enabledCameras.map(camId =>
            testCameraWebSocket(camId, auth, timeout, options)
        )
    );

    // If ANY camera succeeds, server supports WebSockets
    const successfulCamera = results.find(r => r.success);
    if (successfulCamera) {
        debugLog(`[WebSocket Detection] ✓ Server supports WebSockets (camera ${successfulCamera.cameraId} succeeded)`);
        return {
            supported: true,
            testedCameras: enabledCameras
        };
    }

    // All cameras failed
    const errors = results.map(r => r.error).filter(Boolean);
    debugLog(`[WebSocket Detection] ✗ Server does not support WebSockets (all ${enabledCameras.length} cameras failed)`);

    return {
        supported: false,
        testedCameras: enabledCameras,
        error: errors.length > 0 ? errors[0] : 'All camera tests failed'
    };
};

/**
 * Checks client (browser) and server WebSocket support independently
 * Returns detailed diagnostics for UI feedback
 *
 * @param {Object} auth - Authentication object
 * @param {string} [auth.session] - Session token (preferred)
 * @param {string} [auth.username] - Username (fallback)
 * @param {string} [auth.password] - Password (fallback)
 * @param {Array<{bID: number, fEnable: boolean}>} cameras - Array of camera objects
 * @param {Object} options - Optional configuration
 * @param {boolean} [options.forceCheck=false] - Skip cache and force new check
 * @param {string} [options.host] - Override host (for cache key and testing)
 * @param {string} [options.port] - Override port (for cache key and testing)
 * @returns {Promise<{
 *   supported: boolean,
 *   clientSupport: boolean,
 *   serverSupport: boolean,
 *   testedCameras: number[],
 *   error?: string
 * }>}
 */
/**
 * hasWebCodecs — sync check for the browser WebCodecs API (VideoDecoder + EncodedVideoChunk),
 * which is only exposed in a SECURE CONTEXT (HTTPS or localhost). This is the coarse v1 gate for
 * whether ANY ws-canvas overlay (object boxes, LPR, VMD motion windows) can render: over plain
 * HTTP the API is undefined, live falls back to JPEG, and no overlay is possible — so the
 * Object-Detection / License-Plate toggles are dead switches and should be hidden. Uniform across
 * products (cloud/eview require WebCodecs to stream at all, so they read true in practice).
 * Server-side per-NVR ws support + per-eview-member + OD/LPR capability gating is a v2 refinement.
 */
export const hasWebCodecs = () =>
    typeof window !== 'undefined' &&
    typeof window.VideoDecoder !== 'undefined' &&
    typeof window.EncodedVideoChunk !== 'undefined';

export const canUseWebSockets = async (auth, cameras = [], options = {}) => {
    const { forceCheck = false, host, port } = options;

    // Derive cache key from host/port (use window.location if not provided)
    const cacheHost = host || (typeof window !== 'undefined' ? window.location.hostname : 'unknown');
    const cachePort = port || (typeof window !== 'undefined' ? (window.location.port || (window.location.protocol === 'https:' ? '443' : '80')) : '80');
    const cacheKey = `ws_support_${cacheHost}_${cachePort}`;
    const cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours

    const authType = auth.session ? 'session' : 'user/pass';
    debugLog(`[WebSocket Detection] canUseWebSockets(auth=${authType}, cameras=${cameras.length}, forceCheck=${forceCheck})`);

    // Check 1: Client support (WebCodecs API)
    const clientSupport = typeof window !== 'undefined' &&
                          typeof window.VideoDecoder !== 'undefined' &&
                          typeof window.EncodedVideoChunk !== 'undefined';

    debugLog(`[WebSocket Detection] Client WebCodecs support: ${clientSupport ? '✓' : '✗'}`);

    // Check cache for server support (unless force check)
    // Only use POSITIVE cached results - negative results should always be retested
    let cachedServerSupport = null;
    if (!forceCheck) {
        try {
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                const { serverSupport: cachedSupport, timestamp } = JSON.parse(cached);
                const age = Date.now() - timestamp;

                // Only use cache if value is TRUE and not expired
                // Negative results are not cached (always retest on failure)
                if (cachedSupport === true && age < cacheExpiry) {
                    debugLog(`[WebSocket Detection] Using cached server result: ${cachedSupport} (age: ${Math.round(age / 1000)}s)`);
                    cachedServerSupport = cachedSupport;
                } else if (cachedSupport === false) {
                    // Clear stale negative cache - we'll retest
                    debugLog(`[WebSocket Detection] Clearing stale negative cache, will retest`);
                    localStorage.removeItem(cacheKey);
                } else if (typeof cachedSupport !== 'boolean') {
                    debugLog(`[WebSocket Detection] Ignoring invalid cached value: ${cachedSupport}`);
                    localStorage.removeItem(cacheKey);
                }
            }
        } catch (e) {
            // Ignore cache errors
        }
    }

    // Check 2: Server support (always test if not cached, regardless of client support)
    let serverSupport;
    let testedCameras = [];
    let serverError;

    if (cachedServerSupport !== null) {
        serverSupport = cachedServerSupport;
        testedCameras = []; // Not tested this time, used cache
    } else {
        debugLog('[WebSocket Detection] Testing server WebSocket support...');
        let serverResult = await detectWebSocketSupport(auth, cameras, options);

        // If first attempt fails, wait and retry once (handles race conditions)
        if (!serverResult.supported) {
            debugLog('[WebSocket Detection] First attempt failed, retrying in 1s...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            serverResult = await detectWebSocketSupport(auth, cameras, options);
        }

        serverSupport = serverResult.supported;
        testedCameras = serverResult.testedCameras || [];
        serverError = serverResult.error;

        // Only cache POSITIVE results - don't cache failures
        // This way transient failures (race conditions) don't block WebSocket for 24 hours
        if (serverSupport) {
            try {
                localStorage.setItem(cacheKey, JSON.stringify({
                    serverSupport,
                    timestamp: Date.now()
                }));
                debugLog(`[WebSocket Detection] Cached server result: ${serverSupport}`);
            } catch (e) {
                debugWarn('[WebSocket Detection] Failed to cache result:', e);
            }
        } else {
            // Clear any stale negative cache
            try {
                localStorage.removeItem(cacheKey);
            } catch (e) { /* ignore */ }
            debugLog(`[WebSocket Detection] Not caching negative result (will retry on next load)`);
        }
    }

    debugLog(`[WebSocket Detection] Server WebSocket support: ${serverSupport ? '✓' : '✗'}`);

    // Build result with full diagnostics
    const supported = clientSupport && serverSupport;

    const result = {
        supported,
        clientSupport,
        serverSupport,
        testedCameras
    };

    // Add helpful error message for diagnostics
    if (!supported) {
        if (!clientSupport && !serverSupport) {
            result.error = 'Browser and server do not support WebSocket streaming';
        } else if (!clientSupport) {
            result.error = 'Browser does not support WebCodecs API (try Chrome, Edge, or Safari)';
        } else if (!serverSupport) {
            result.error = serverError || 'Server does not support WebSocket streaming';
        }
    }

    debugLog(`[WebSocket Detection] Final result: supported=${supported}, client=${clientSupport}, server=${serverSupport}`);

    return result;
};
